import { useMemo } from 'react';
import { Reorder } from 'motion/react';
import { GripVertical } from 'lucide-react';
import { ORDER_MODES, ORDER_MODE_DESCRIPTIONS, orderBattingLineup } from '../../lib/batting-order';
import type { BatterSeasonSignal } from '../../lib/insights/season-signals';
import { ReorderRow } from '../ReorderRow';
import { WizardBar, WizardHead, WizardFoot, WizardSelect } from './WizardChrome';

// Step 3. The order, with the reason for every slot.
//
// The reason line is the whole point: orderBattingLineup already returns a
// rationale per player and the old screen barely showed it. That is the thing
// GameChanger does not do, so it goes under every name.
//
// Drag uses the shared ReorderRow: handle-only (a flick on the card body scrolls),
// lifted above its neighbours while held, haptic on pick up, swap and drop.

export const StepOrder = ({
  activePlayers, batterSignals, orderMode, orderNote, stepLabel,
  onApplyOrder, onReorder, onBack, onContinue, onSkip,
}: {
  activePlayers: any[];
  batterSignals: Map<string, BatterSeasonSignal>;
  orderMode: string;
  orderNote: string;
  stepLabel: string;
  onApplyOrder: (mode: string) => void;
  onReorder: (players: any[]) => void;
  onBack: () => void;
  onContinue: () => void;
  onSkip: () => void;
}) => {
  // Reasons come from the engine for the CURRENT mode, keyed by player, so they
  // survive a manual drag instead of resetting the list.
  const reasons = useMemo(() => {
    const out = orderBattingLineup(activePlayers, batterSignals, orderMode);
    return new Map(out.map(r => [r.player.id, r.reason]));
  }, [activePlayers, batterSignals, orderMode]);

  // The two data-driven modes ('Get Baserunners', 'Wear Down Pitcher') need
  // imported GameChanger at-bats to mean anything; with none, hide them rather
  // than show a mode that silently falls back to something else. A disabled
  // <option> in a native <select> can't carry an explanatory tooltip the way a
  // button can (LineupBuilder's custom dropdown does that instead) — hiding is
  // the honest choice here, not just the simpler one.
  //
  // `orderMode` can only ever become 'Get Baserunners'/'Wear Down Pitcher' via
  // onApplyOrder while this step is open with data already loaded, and
  // batterSignals never shrinks over the wizard's lifetime — so the applied
  // mode can't be orphaned by this filter today. Filtering the <select>'s
  // OPTIONS is enough; no fallback/reset needed for a case that can't occur.
  const visibleOrderModes = batterSignals.size > 0 ? ORDER_MODES : ORDER_MODES.filter(m => !m.data);

  const ids = activePlayers.map((p: any) => p.id);
  const byId = new Map(activePlayers.map((p: any) => [p.id, p]));

  return (
    <>
      <WizardBar label={stepLabel} onBack={onBack} />
      <WizardHead
        title="Batting Order"
        sub={orderNote || 'Drag the handle to move a player.'}
      />

      <div className="flex-1 overflow-y-auto bg-canvas px-4 pt-4">
        <div className="mb-3.5">
          {/* Static "what this algorithm does" lives under the select (via
              `description`, matching Step 2's Defense picker) so a coach reads
              it right where they pick, not only after. `orderNote` above stays
              reserved for the two data-driven modes' dynamic, per-roster
              rationale (who actually leads off and why) - a different fact from
              the generic description, so it is never duplicated on screen. */}
          <WizardSelect
            label="Auto lineup"
            value={orderMode}
            options={visibleOrderModes.map(m => ({ key: m.key, label: m.label, description: ORDER_MODE_DESCRIPTIONS[m.key] }))}
            onChange={onApplyOrder}
          />
        </div>

        {activePlayers.length === 0 ? (
          <p className="text-sm text-ink-2 px-1 py-6 text-center">
            Everyone is marked out. Turn someone back on in Availability.
          </p>
        ) : (
          <Reorder.Group
            axis="y"
            values={ids}
            onReorder={(newIds: string[]) => onReorder(newIds.map(id => byId.get(id)).filter(Boolean))}
            className="bg-surface rounded-panel overflow-hidden list-none"
          >
            {ids.map((id, i) => {
              const p = byId.get(id);
              if (!p) return null;
              return (
                <ReorderRow key={id} value={id}>
                  {(controls) => (
                    <div
                      className={`flex items-center gap-3 px-4 py-3 bg-surface ${i === ids.length - 1 ? '' : 'border-b border-hairline'}`}
                    >
                      <span className="w-4 shrink-0 text-base font-bold text-ink-mute tabular-nums">{i + 1}</span>
                      <span className="flex-1 min-w-0">
                        <span className="block text-base font-semibold tracking-[-0.012em] text-ink truncate">
                          {p.name}
                        </span>
                        <span className="block text-[13.5px] text-ink-2 mt-0.5 leading-snug">
                          {reasons.get(id) || ''}
                        </span>
                      </span>
                      {/* Only this handle starts a drag, so the list still scrolls. */}
                      <button
                        onPointerDown={e => controls.start(e)}
                        aria-label={`Move ${p.name}`}
                        className="shrink-0 -mr-1 p-1 touch-none cursor-grab active:cursor-grabbing"
                      >
                        <GripVertical className="w-[18px] h-[18px] text-ink-mute" />
                      </button>
                    </div>
                  )}
                </ReorderRow>
              );
            })}
          </Reorder.Group>
        )}
        <div className="h-4" />
      </div>

      <WizardFoot
        primaryLabel="Continue"
        onPrimary={onContinue}
        secondaryLabel="Skip this step"
        onSecondary={onSkip}
      />
    </>
  );
};
